import React, { useState } from 'react'
import { Button } from './button'
import { ChevronDown, Type, DollarSign, Percent, Calendar, Mail, Phone, Hash, ChevronRight } from 'lucide-react'
import { ColumnType } from '../../lib/smartFormatter'

interface DataTypeSelectorProps {
  currentType: ColumnType
  onTypeChange: (newType: ColumnType['type']) => void
  onFormatChange?: (updates: Partial<ColumnType>) => void
  className?: string
}

const DATA_TYPES = [
  { type: 'text' as const, label: 'Text', icon: Type, description: 'Plain text' },
  { type: 'number' as const, label: 'Number', icon: Hash, description: 'Numeric values' },
  { type: 'currency' as const, label: 'Currency', icon: DollarSign, description: 'Money values' },
  { type: 'percentage' as const, label: 'Percentage', icon: Percent, description: 'Percentage values' },
  { type: 'date' as const, label: 'Date', icon: Calendar, description: 'Date values' },
  { type: 'email' as const, label: 'Email', icon: Mail, description: 'Email addresses' },
  { type: 'phone' as const, label: 'Phone', icon: Phone, description: 'Phone numbers' },
]

const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
]

const DATE_FORMATS = [
  { key: 'short', label: 'Mar 15, 2020' },
  { key: 'long', label: 'March 15, 2020' },
  { key: 'numeric', label: '3/15/2020' },
  { key: 'iso', label: '2020-03-15' },
  { key: 'year-month', label: 'Mar 2020' },
  { key: 'year-only', label: '2020' },
]

export function DataTypeSelector({ currentType, onTypeChange, onFormatChange, className = '' }: DataTypeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredType, setHoveredType] = useState<string | null>(null)
  
  const currentTypeInfo = DATA_TYPES.find(t => t.type === currentType.type) || DATA_TYPES[0]
  const CurrentIcon = currentTypeInfo.icon

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="h-6 w-6 p-0 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Data type: ${currentTypeInfo.label}`}
      >
        <CurrentIcon className="h-3 w-3" />
      </Button>
      
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20">
            <div className="py-1">
              <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                Data Type
              </div>
              {DATA_TYPES.map(({ type, label, icon: Icon, description }) => (
                <button
                  key={type}
                  onClick={() => {
                    if (type !== 'currency' && type !== 'date') {
                      onTypeChange(type)
                      setIsOpen(false)
                    } else {
                      onTypeChange(type)
                      if (!onFormatChange) {
                        setIsOpen(false)
                      }
                    }
                  }}
                  onMouseEnter={() => setHoveredType(type)}
                  onMouseLeave={() => setHoveredType(null)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 relative ${
                    currentType.type === type ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-gray-500">{description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentType.type === type && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                    {(type === 'currency' || type === 'date') && onFormatChange && (
                      <ChevronRight className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Secondary dropdown for currency/date */}
                  {hoveredType === type && (type === 'currency' || type === 'date') && onFormatChange && (
                    <div className="absolute left-full top-0 ml-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                      {type === 'currency' && (
                        <div className="py-2">
                          <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                            Currency Options
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {CURRENCY_OPTIONS.map(({ code, symbol, name }) => (
                              <button
                                key={code}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onFormatChange({ currency: code })
                                  setIsOpen(false)
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                                  currentType.currency === code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="font-mono">{symbol}</span>
                                  <span>{name}</span>
                                </span>
                                <span className="text-xs text-gray-500">{code}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {type === 'date' && (
                        <div className="py-2">
                          <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                            Date Format Options
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {DATE_FORMATS.map(({ key, label: formatLabel }) => (
                              <button
                                key={key}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onFormatChange({ dateFormat: key })
                                  setIsOpen(false)
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                                  currentType.dateFormat === key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                }`}
                              >
                                <span>{formatLabel}</span>
                                {currentType.dateFormat === key && (
                                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}